import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import { PairDeviceProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import { ExchangeDeviceCodeResponse, GetDeviceCodeResponse, requestDeviceCode } from '../../utils/pairing';

const trimEnd = (u: string) => u.replace(/\/+$/, '');
const ensurePath = (base: string, needed: string) =>
	base.includes(needed) ? base : `${trimEnd(base)}${needed}`;

export const debugLogResponse = async (label: string, resp: Response) => {
	const clone = resp.clone();
	let body: any;
	try {
		body = await clone.json();
	} catch {
		try {
			body = await clone.text();
		} catch {
			body = '<no body>';
		}
	}
	const headers: Record<string, string> = {};
	resp.headers.forEach((v, k) => (headers[k] = v));
	console.debug(`[${label}] –`, {
		url: resp.url,
		status: resp.status,
		ok: resp.ok,
		headers,
		body,
	});
	return body;
};

export default function PairDevice(props: PairDeviceProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);
	const [deviceData, setDeviceData] = useState<GetDeviceCodeResponse>();
	const [countdown, setCountdown] = useState<number>(900);
	const [statusMsg, setStatusMsg] = useState<string>('');
	const [errorMsg, setErrorMsg] = useState<string>('');
	const [isDelivering, setIsDelivering] = useState<boolean>(false);
	const [deliveryAccessToken, setDeliveryAccessToken] = useState<
		string | null
	>(null);

	const useQs = localStorage.getItem('use_qs') === 'true';

	const qsBase = useMemo(() => {
		const ls = localStorage.getItem('qs_url') || '';
		return trimEnd((state.market?.qsUrl || ls || '').toString());
	}, [state.market?.qsUrl]);

	const livePairingHost = useMemo(() => {
		const ep =
			state.market?.urls?.pairing ||
			localStorage.getItem('url_pairing') ||
			'';
		return ep ? trimEnd(ep) : '';
	}, [state.market?.urls?.pairing]);

	const liveDeliveryHost = useMemo(() => {
		const ep =
			state.market?.urls?.delivery ||
			localStorage.getItem('url_delivery') ||
			'';
		return ep ? trimEnd(ep) : '';
	}, [state.market?.urls?.delivery]);

	const pairingBase = useMemo(() => {
		if (useQs && qsBase) return ensurePath(qsBase, '/module/devices/bpca');
		if (livePairingHost)
			return ensurePath(livePairingHost, '/module/devices/bpca');
		if (qsBase) return ensurePath(qsBase, '/module/devices/bpca');
		return '';
	}, [useQs, qsBase, livePairingHost]);

	const deliveryBase = useMemo(() => {
		if (useQs && qsBase) return ensurePath(qsBase, '/module/delivery/bpca');
		if (liveDeliveryHost)
			return ensurePath(liveDeliveryHost, '/module/delivery/bpca');
		if (qsBase) return ensurePath(qsBase, '/module/delivery/bpca');
		return '';
	}, [useQs, qsBase, liveDeliveryHost]);

	const stopRef = useRef(false);
	const tokenPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null
	);
	const deliveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const deliveryStartedRef = useRef<boolean>(false);

	const clearTokenTimer = () => {
		if (tokenPollTimerRef.current) {
			clearTimeout(tokenPollTimerRef.current);
			tokenPollTimerRef.current = null;
		}
	};
	const clearDeliveryTimer = () => {
		if (deliveryTimerRef.current) {
			clearTimeout(deliveryTimerRef.current);
			deliveryTimerRef.current = null;
		}
	};
	const clearNavTimer = () => {
		if (navTimerRef.current) {
			clearTimeout(navTimerRef.current);
			navTimerRef.current = null;
		}
	};

	// ----- Countdown -----
	useEffect(() => {
		if (!countdown) return;

		const id = setInterval(() => {
			setCountdown(prev => {
				if (prev > 1) return prev - 1;

				// countdown reached 0, fetch new device code
				(async () => {
					try {
						const dev = await requestDeviceCode(pairingBase);
						setDeviceData(dev);
						setCountdown(dev.expires_in || 900);
					} catch (err) {
						console.error("Failed to refresh device code:", err);
					}
				})();

				return 0; // temporarily set to 0 until fetch updates it
			});
		}, 1000);

		return () => clearInterval(id);
	}, [countdown, pairingBase]);

	useEffect(() => {
		const resume = localStorage.getItem('pairing') === 'true';
		if (!resume && (!pairingBase || !deliveryBase)) {
			setStatusMsg(t('PairingErrorUrlMissing'));
			return;
		}

		stopRef.current = false;
		deliveryStartedRef.current = false;

		const startFlow = async () => {
			try {
				setStatusMsg(t('PairingStarted'));
				setErrorMsg('');

				const lsIdToken = localStorage.getItem('id_token') || '';
				const lsRefresh = localStorage.getItem('refresh_token') || '';
				if (resume && lsIdToken && lsRefresh) {
					const existingActivation: ExchangeDeviceCodeResponse = {
						id_token: lsIdToken,
						refresh_token: lsRefresh,
						token_type: 'Bearer',
					};
					deliveryStartedRef.current = true;
					setIsDelivering(true);
					setStatusMsg(t('PairingSuccessful'));
					await runDeliveryFlow(existingActivation);
					return;
				}

				// -------- Frischer Flow: Device Code holen + Token poll’en ----------
				const dev = await requestDeviceCode(pairingBase);
				setDeviceData(dev);
				setCountdown(dev.expires_in || 900);

				const { device_code, interval, expires_in } = dev;

				let delayMs = Math.max(1000, (interval || 5) * 1000);
				const BASE_DELAY = delayMs;
				const MAX_DELAY = 15000;

				setStatusMsg(t('PairingInstruction'));

				const scheduleTokenPoll = (ms: number) => {
					if (stopRef.current || deliveryStartedRef.current) return;
					tokenPollTimerRef.current = setTimeout(tokenPoll, ms);
				};

				const tokenPoll = async () => {
					if (stopRef.current || deliveryStartedRef.current) return;
					if (countdown === 0) {
						setStatusMsg(t('PairingErrorCodeExpired'));
						return;
					}
					try {
						const formData = new FormData();
						formData.append('device_code', device_code);
						formData.append('client_id', device_code);
						const tokenUrl = `${pairingBase}/oauth/device/token`;
						const response = await fetch(tokenUrl, {
							method: 'POST',
							headers: { 'X-Requested-With': 'XMLHttpRequest' },
							body: formData,
						});

						const body = await debugLogResponse('device/token', response);

						if (response.status === 200) {
							const activationResponse: ExchangeDeviceCodeResponse =
								body;
							localStorage.setItem('pairing', 'true');
							localStorage.setItem(
								'id_token',
								activationResponse.id_token
							);
							localStorage.setItem(
								'refresh_token',
								activationResponse.refresh_token
							);
							localStorage.setItem('device_code', device_code);


							// AppState
							dispatch({ type: 'SET_PINPAIRING_DEVICECODE', value: device_code });
							dispatch({ type: 'SET_PINPAIRING_ACTIVATIONRESPONSE', value: activationResponse });
							dispatch({ type: 'SET_DEVICE', value: activationResponse.id_token });
							dispatch({ type: 'SET_PAIRING', value: true });


							deliveryStartedRef.current = true;
							clearTokenTimer();
							setIsDelivering(true);
							setStatusMsg(t('PairingSuccessful'));
							await runDeliveryFlow(activationResponse);
							return;
						}

						if (
							response.status === 204 ||
							response.status === 403
						) {
							delayMs = BASE_DELAY;
							scheduleTokenPoll(delayMs);
							return;
						}

						if (response.status === 429) {
							const ra = response.headers.get('Retry-After');
							let backoff;
							if (ra) {
								const n = Number(ra);
								backoff =
									!Number.isNaN(n) && n > 0
										? Math.min(MAX_DELAY, n * 1000)
										: Math.min(MAX_DELAY, delayMs * 2);
							} else {
								backoff = Math.min(MAX_DELAY, delayMs * 2);
							}
							delayMs = backoff;
							scheduleTokenPoll(delayMs);
							return;
						}

						scheduleTokenPoll(delayMs);
					} catch {
						scheduleTokenPoll(delayMs);
					}
				};

				tokenPoll();
			} catch (e: any) {
				setErrorMsg(e?.message || 'Fehler beim Start des Pairings.');
			}
		};

		startFlow();
		return () => {
			stopRef.current = true;
			clearTokenTimer();
			clearDeliveryTimer();
			clearNavTimer();
		};
	}, [pairingBase, deliveryBase, dispatch, t]);

	const getDeliveryAccessTokenWithBackoff = async (
		act: ExchangeDeviceCodeResponse
	): Promise<string | null> => {
		let delay = 3000;
		const MAX_DELAY = 15000;
		while (!stopRef.current) {
			const fd = new FormData();
			fd.append('client_id', act.id_token);
			fd.append('client_secret', act.refresh_token);
			fd.append('grant_type', 'client_credentials');
			const resp = await fetch(`${deliveryBase}/api/v2/oauth/token`, {
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				body: fd,
			});

			const body = await debugLogResponse('delivery/oauth/token (cc)', resp);

			if (resp.ok && body?.access_token) {
				localStorage.setItem('accesstoken', body.access_token);
				const expiresAt = Date.now() + body.expires_in * 1000;
				localStorage.setItem('accesstoken_expires_at', expiresAt.toString());
				return body.access_token as string;
			}

			if (resp.status === 404) {
				const ra = resp.headers.get('Retry-After');
				delay =
					ra && !Number.isNaN(+ra) && +ra > 0
						? Math.min(MAX_DELAY, +ra * 1000)
						: Math.min(MAX_DELAY, Math.round(delay * 1.5));
				setStatusMsg(t('PairingErrorMissingDeliveryToken'));
				await new Promise(r => setTimeout(r, delay));
				continue;
			}

			if (resp.status === 429) {
				const ra = resp.headers.get('Retry-After');
				delay =
					ra && !Number.isNaN(+ra) && +ra > 0
						? Math.min(MAX_DELAY, +ra * 1000)
						: Math.min(MAX_DELAY, delay * 2);
				await new Promise(r => setTimeout(r, delay));
				continue;
			}

			await new Promise(r => setTimeout(r, delay));
		}
		return null;
	};

	const refreshDeliveryAccessTokenOn401 = async (
		act: ExchangeDeviceCodeResponse
	): Promise<string | null> => {
		const fd = new FormData();
		fd.append('client_id', act.id_token);
		fd.append('client_secret', act.refresh_token);
		fd.append('grant_type', 'client_credentials');
		const resp = await fetch(`${deliveryBase}/api/v2/oauth/token`, {
			method: 'POST',
			headers: { 'X-Requested-With': 'XMLHttpRequest' },
			body: fd,
		});

		const body = await debugLogResponse('delivery/oauth/token (refresh)', resp);
		if (resp.ok && body?.access_token) {
			return body.access_token as string;
		}
		return null;
	};

	const runDeliveryFlow = async (
		activationResponse: ExchangeDeviceCodeResponse
	) => {
		let access = deliveryAccessToken;
		if (!access) {
			access =
				await getDeliveryAccessTokenWithBackoff(activationResponse);
			if (!access || stopRef.current) {
				setIsDelivering(false);
				setStatusMsg(t('PairingErrorDeliveryAuthentication'));
				return;
			}
			setDeliveryAccessToken(access);
		}

		const baseContentsUrl = `${deliveryBase}/api/v2/contents?fields-preset=full`;
		let delay = 3000;
		const MAX_DELAY = 15000;

		const pollContents = async () => {
			if (stopRef.current) return;

			const resp = await fetch(baseContentsUrl, {
				method: 'GET',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					Authorization: `Bearer ${access}`,
				},
			});
			await debugLogResponse('delivery/contents', resp);

			const body = await debugLogResponse('delivery/contents', resp);

			// 200: fertig, nach 5s weiter zur nächsten Seite
			if (resp.status === 200) {
				setStatusMsg(t('PairingPricesheetLoaded'));
				setIsDelivering(true);
				clearNavTimer();
				navTimerRef.current = setTimeout(() => {
					setIsDelivering(false);
					props.setScreen(ScreenEnum.selectDevice);
				}, 5000);
				return;
			}

			if (resp.status === 204 || resp.status === 404) {
				const ra = resp.headers.get('Retry-After');
				delay = ra && !Number.isNaN(+ra) && +ra > 0
					? Math.min(MAX_DELAY, +ra * 1000)
					: Math.min(MAX_DELAY, delay);
				delay =
					ra && !Number.isNaN(+ra) && +ra > 0
						? Math.min(MAX_DELAY, +ra * 1000)
						: Math.min(MAX_DELAY, delay);
				deliveryTimerRef.current = setTimeout(pollContents, delay);
				return;
			}

			if (resp.status === 401) {
				const renewed =
					await refreshDeliveryAccessTokenOn401(activationResponse);
				if (renewed) {
					access = renewed;
					setDeliveryAccessToken(renewed);
					deliveryTimerRef.current = setTimeout(pollContents, 500);
					return;
				}
				setIsDelivering(false);
				setStatusMsg(t('PairingErrorTokenRenewal'));
				return;
			}

			// 404: „Content … not available“ → einfach weiter warten (kein neues OAuth!)
			if (resp.status === 404) {
				const ra = resp.headers.get('Retry-After');
				delay = ra && !Number.isNaN(+ra) && +ra > 0
					? Math.min(MAX_DELAY, +ra * 1000)
					: Math.min(MAX_DELAY, delay);
				deliveryTimerRef.current = setTimeout(pollContents, delay);
				return;
			}

			if (resp.status === 429) {
				const ra = resp.headers.get('Retry-After');
				delay = ra && !Number.isNaN(+ra) && +ra > 0
					? Math.min(MAX_DELAY, +ra * 1000)
					: Math.min(MAX_DELAY, delay * 2);
				delay =
					ra && !Number.isNaN(+ra) && +ra > 0
						? Math.min(MAX_DELAY, +ra * 1000)
						: Math.min(MAX_DELAY, delay * 2);
				deliveryTimerRef.current = setTimeout(pollContents, delay);
				return;
			}

			// Andere Stati: vorsichtig weiter pollen
			deliveryTimerRef.current = setTimeout(pollContents, delay);
		};

		setIsDelivering(true);
		setStatusMsg(t('PairingSuccessful'));
		pollContents();
	};

	const formatCountdown = (sec: number): string => {
		const date = new Date(sec * 1000);
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const seconds = String(date.getSeconds()).padStart(2, '0');
		return `${minutes}:${seconds}`;
	};

	const userCode = deviceData?.user_code || '';

	return (
		<>
			<div className='top'>
				<div className='pair-device'>
					{props.backBtn(ScreenEnum.selectMarket)}
					<h1>{t('PairDeviceHeadline')}</h1>
					<p className='p1'>{t('PairDeviceText')}</p>
					<Progress
						screen={ScreenEnum.pairDevice}
						setScreen={props.setScreen}
					/>
				</div>

				<div className='pair-device-content'>
					<div className='pin-pairing-pin'>
						{userCode ? (
							userCode.split('').map((char, index) => (
								<div className='pin-pairing-box' key={index}>
									<p>{char}</p>
								</div>
							))
						) : (
							<div className='pin-pairing-box'>
								<p>…</p>
							</div>
						)}
					</div>

					<p>
						{t('PinPairingRefresh', {
							countdown: formatCountdown(countdown),
						})}
					</p>

					{statusMsg && (
						<p className='p1' aria-live='polite'>
							{statusMsg}
						</p>
					)}
					{errorMsg && <p className='p1 error-message'>{errorMsg}</p>}
				</div>
			</div>

			<div
				className={`delivery-overlay ${isDelivering ? 'show' : ''}`}
				aria-live='assertive'
				role='status'
			>
				<div className='content'>
					<p>{statusMsg}</p>
					<div className='spinner' />
				</div>
			</div>

			<div className='buttons'>{props.cancelBtn}</div>
		</>
	);
}
